/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowUpRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, LineReveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";

const SERVICES = [
  "Ristrutturazione chiavi in mano",
  "Impermeabilizzazione in poliurea",
  "Coibentazione in poliuretano",
  "Efficientamento energetico (fotovoltaico)",
  "Pareti ventilate",
  "Rivestimenti per piscine",
  "Rivestimenti protettivi anticorrosivi",
  "Restauro conservativo",
  "Finiture e rivestimenti (umidità di risalita)",
  "Altro / Sopralluogo",
];

const EMPTY = { name: "", email: "", phone: "", service: "", location: "", message: "" };

export default function Preventivo() {
  const [form, setForm] = useState(() => {
    const svc = new URLSearchParams(window.location.search).get("servizio");
    return { ...EMPTY, service: svc && SERVICES.includes(svc) ? svc : "" };
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    setLoading(true);
    try {
      // If the visitor came from a project page, include the slug so the
      // backend can attribute the conversion to that project.
      let project_slug = null;
      try { project_slug = sessionStorage.getItem("lastViewedProject") || null; } catch {}
      await api.post("/quotes", { ...form, project_slug });
      setSent(true);
      toast.success("Richiesta inviata! Ti risponderemo al più presto.");
      setForm(EMPTY);
      try { sessionStorage.removeItem("lastViewedProject"); } catch {}
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nell'invio.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="preventivo-page" className="bg-background">
      <section className="pt-40 pb-16 md:pt-48 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative max-w-[1500px] mx-auto px-5 md:px-10">
          <div className="font-mono text-xs uppercase tracking-widest text-primary mb-8 flex items-center gap-3">
            <span>/ Preventivo</span><span className="w-16 h-px bg-primary" />
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-16">
            <h1 className="font-head font-black text-[1.9rem] min-[380px]:text-[2.3rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tightest leading-[0.95] flex-1">
              <LineReveal lines={["Richiedi un", "preventivo."]} />
            </h1>
            <img
              src="/images/ghs-logo-full.png?v=4"
              alt="GHS SpA — Greenhouse Solutions SPA"
              width="636"
              height="340"
              data-testid="preventivo-logo"
              className="w-52 sm:w-64 md:w-80 lg:w-96 h-auto shrink-0 self-start lg:self-end lg:mb-3"
              loading="eager"
            />
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Descrivici il tuo intervento: ti ricontatteremo per un sopralluogo e un
                preventivo gratuito e senza impegno.
              </p>
              <div className="mt-10 space-y-6 font-mono text-sm">
                <div className="border-l-2 border-primary pl-4">
                  <p className="text-muted-foreground text-xs uppercase tracking-widest">Zona</p>
                  <p className="font-medium mt-1">Puglia · Tutta Italia</p>
                </div>
                <div className="border-l-2 border-primary pl-4">
                  <p className="text-muted-foreground text-xs uppercase tracking-widest">Risposta</p>
                  <p className="font-medium mt-1">Entro 24-48h</p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            {sent ? (
              <div data-testid="preventivo-success" className="border border-border bg-card p-12 flex flex-col items-start">
                <CheckCircle2 className="w-14 h-14 text-primary mb-6" />
                <h2 className="font-head font-black text-3xl md:text-4xl tracking-tightest mb-4">Richiesta ricevuta.</h2>
                <p className="text-muted-foreground leading-relaxed max-w-lg">
                  Grazie per averci contattato. Un nostro tecnico esaminerà la tua richiesta e ti
                  risponderà al più presto.
                </p>
                <p data-testid="preventivo-success-email-note" className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-lg border-l-2 border-primary pl-4">
                  Ti abbiamo inviato una <strong className="text-foreground">email di conferma</strong> con il riepilogo della richiesta.
                  Se non la vedi, controlla la cartella spam.
                </p>
                <button
                  onClick={() => setSent(false)}
                  data-testid="preventivo-new"
                  className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-b-2 border-primary pb-1 hover:gap-3 transition-all"
                >
                  Invia un'altra richiesta <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="preventivo-form" className="border border-border bg-card p-8 md:p-12 space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-mono text-xs uppercase tracking-widest">Nome e cognome *</Label>
                    <Input id="name" data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Mario Rossi" className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-mono text-xs uppercase tracking-widest">Telefono *</Label>
                    <Input id="phone" data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="+39 ..." className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest">Email *</Label>
                    <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} placeholder="mario@email.it" className="rounded-none h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location" className="font-mono text-xs uppercase tracking-widest">Località</Label>
                    <Input id="location" data-testid="input-location" value={form.location} onChange={set("location")} placeholder="Bari, BA" className="rounded-none h-12" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-widest">Servizio richiesto *</Label>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger data-testid="select-service" className="rounded-none h-12">
                      <SelectValue placeholder="Seleziona un servizio" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="font-mono text-xs uppercase tracking-widest">Descrivi l'intervento</Label>
                  <Textarea id="message" data-testid="input-message" value={form.message} onChange={set("message")} rows={5} placeholder="Metratura, tipo di immobile, tempistiche desiderate..." className="rounded-none" />
                </div>

                <button
                  type="submit"
                  data-testid="preventivo-submit"
                  disabled={loading}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-widest px-10 py-5 hover:bg-foreground transition-colors disabled:opacity-60"
                >
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Invio...</> : <>Invia richiesta <ArrowUpRight className="w-5 h-5" /></>}
                </button>

                <p data-testid="preventivo-privacy-note" className="mt-4 text-xs text-muted-foreground leading-relaxed">
                  Inviando il modulo dichiari di aver letto l'{" "}
                  <Link to="/privacy" className="text-foreground underline underline-offset-2 hover:text-primary transition-colors">informativa privacy</Link>:
                  i tuoi dati saranno utilizzati solo per ricontattarti ed elaborare il preventivo.
                </p>

                <p data-testid="preventivo-pec-note" className="mt-6 text-xs text-muted-foreground font-mono uppercase tracking-widest">
                  Per comunicazioni ufficiali: PEC{" "}
                  <a href="mailto:greenhousesolutionsspa@pec.it" className="text-foreground hover:text-primary transition-colors normal-case tracking-normal">
                    greenhousesolutionsspa@pec.it
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
